<template>
  <v-form ref="form">
    <v-toolbar flat class="mb-2">
      <h2 v-if="title">{{ title }}</h2>
      <v-spacer/>
      <v-btn @click="addTask(null, {})" data-test="add-new-task">
        <v-icon class="px-1" left>add</v-icon>
        Add New Task
      </v-btn>
    </v-toolbar>
    <transition-group name="cards"
                      tag="div"
                      class="layout row wrap"
                      v-if="tasks.length !== 0">
        <v-flex v-for="(task, i) in tasks" :key="`task-${i}`" xs6>
        <v-card class="ma-1">
            <v-toolbar transparent flat dense>
            <v-toolbar-title>Task {{i+1}}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="tasks.splice(i, 1)">
              <v-icon>clear</v-icon>
            </v-btn>
            </v-toolbar>
            <v-card-text class="px-2 pb-0 mb-0">
            <task-types-select
                required
                item-value="id"
                @change="task.config = {};"
                v-model="task.id"
                :data-test="`taskType-${i}`"
                :data-test-task-type-id="task.id"
                :data-test-task-type-config="task.config || {}"/>
            </v-card-text>
            <v-card-text class="pt-0 pb-4 px-4" v-if="taskTypes[task.id]">
                <task-json-editor v-if="!isSchemaEmpty(task.id)"
                                  :task-type-id="taskTypes[task.id].id"
                                  v-model="task.config"
                                  @selectedExistingTask="task.config = $event"
                                  :name.sync="task.name"
                                  :key="`${configId}-${task.id}`"
                                  :schema="getSchema(task.id)"
                                  :data-test="`taskTypeJsonEditor-${i}`"/>
                <template v-else>
                  <v-alert color="secondary lighten-1" :value="true">
                  No configuration available for this task.
                  </v-alert>
                </template>
                <v-alert icon="warning" color="error" :value="validationErrors[i].length === 1" outlined class="mt-2">
                  <strong>Task Operation Validation Error</strong>
                  <div color="error" v-for="(validationError, j) in validationErrors[i]" :key="`error-${j}`">
                    {{ validationError }}
                  </div>
                </v-alert>
            </v-card-text>
        </v-card>
      </v-flex>
    </transition-group>
  </v-form>
</template>

<script>
import { mapState } from 'vuex'
import clonedeep from 'lodash.clonedeep'
import TaskJsonEditor from '@/components/JsonEditor/TaskJsonEditor'
import TaskTypesSelect from '@/components/Tasks/TaskTypesSelect'

export default {
  name: 'TaskConfig',
  components: {
    TaskJsonEditor,
    TaskTypesSelect
  },
  props: {
    config: Object,
    title: String
  },
  data () {
    return {
      configId: 1,
      tasks: [],
      validationErrors: []
    }
  },
  mounted () {
    this.loadConfig()
  },
  computed: {
    ...mapState('tasks', {
      taskTypes: 'taskTypes'
    })
  },
  watch: {
    config () {
      this.loadConfig()
    },
    tasks: {
      handler () {
        this.updateConfig()
      },
      deep: true
    }
  },
  methods: {
    /**
     * Add a new task to the custom tasks list
     */
    addTask (id, config) {
      this.tasks.push({
        id: id,
        config: clonedeep(config)
      })
      this.validationErrors.push([])
    },

    updateConfig () {
      if (this.validateConfig()) {
        this.saveConfig()
      }
    },

    /**
     * Validate the config
     */
    validateConfig () {
      let valid = true
      for (let i in this.tasks) {
        let task = this.tasks[i]
        if (!task.id) {
          continue
        }

        this.validationErrors[i].splice(0, this.validationErrors[i].length)
        for (let operation of (task.config.operations || [])) {
          if (operation.path && !(operation.path in task.config)) {
            this.validationErrors[i].push(`Unknown path "${operation.path}"`)
            valid = false
          }
        }
      }
      return valid
    },

    /**
     * Save the config from internal format
     */
    saveConfig () {
      this.config.names = []
      this.config.parameters = {}
      for (let task of this.tasks) {
        if (!task.id) {
          continue
        }
        let name = this.taskTypes[task.id].name
        this.config.names.push(name)
        this.config.parameters[name] = clonedeep(task.config)
      }
    },
    /**
     * Load the config and convert to internal format
     */
    loadConfig () {
      // This used to generate a unique key for the schema editor
      // otherwise we get caching errors
      this.configId++

      const taskTypesByName = {}
      for (let taskType of Object.values(this.taskTypes)) {
        taskTypesByName[taskType.name] = taskType
      }

      this.tasks = []
      for (let name of (this.config.names || [])) {
        this.addTask(taskTypesByName[name].id, this.config.parameters[name])
      }
    },
    /**
     * Returns true if the task type doesn't have fields defined in its json schema
     *
     * @param taskType
     */
    isSchemaEmpty (taskTypeId) {
      const taskSchema = this.taskTypes[taskTypeId]['json_schema']
      return !taskSchema || Object.keys(taskSchema).length === 0
    },
    getSchema (taskTypeId) {
      const taskSchema = this.taskTypes[taskTypeId]['json_schema']
      if ('properties' in taskSchema) {
        taskSchema.properties.operations = {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              op: { type: 'string', enum: this.$store.state.tasks.taskOperations },
              path: { type: 'string' },
              input: { type: 'array', items: { type: 'string' } }
            },
            required: ['op', 'path', 'input']
          }
        }
      }
      return taskSchema
    }
  }
}
</script>

<style lang="sass">
.task-row
  flex-wrap: wrap !important

  .task-col
    max-width: 33% !important

.task-config
  margin-top: -16px

  ul
    list-style-type: none

.cards-enter-active
  transition: all 0.2s

.cards-enter
  opacity: 0
  transform: translateY(30px)
</style>
